import React, { createContext, useReducer, useEffect } from 'react';
import dashboardReducer from '../reducers/dashboardReducer';
import types from '../types';
import utils, {
  formattedMonth,
  getThisMonthsRides,
  getTodaysRides,
  timestampFormat,
} from '../utils';

const {
  SET_GLOBAL_ERROR,
  SET_LOADING,
  SET_AUTH_ERROR,
  SET_INIT_LOADING,
  SET_FEEDBACKS,
  SET_USERS,
  SET_DRIVERS,
  SET_RIDES,
  SET_RIDES_REALTIME_STAT,
  SET_CURRENT_CAR_PRICES,
  SET_SUCCESS,
  SET_DELETE_SUCCESS,
} = types;

const { getCookie, deleteCookie } = utils;

const initialState = {
  admin: 'Elijah_trillionz',
  error: false,
  globalError: false,
  loading: false,
  initLoading: true,
  done: false,
  authError: false,
  success: false,
  deleteSuccess: false,
  feedbacks: [],
  users: [],
  drivers: [],
  rides: [],
  ridesRealtimeStat: {
    month: 0,
    today: 0,
  },
  currentPrices: {
    luxury: 0,
    premium: 0,
  },
};

export const DashboardContext = createContext(initialState);

export const DashboardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const access_token = getCookie('_access_token_');
  const uri = 'https://liontaxi-api.emmalion.com/api';

  const getFeedbacks = async () => {
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/feedbacks`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setDispatch(SET_FEEDBACKS, jsonRes.reverse());
      } else {
        if (jsonRes.statusCode === 401) {
          logAdminOut();
          setGlobalError('Your session has expired');
        } else setGlobalError(jsonRes.message);
      }
    } catch (err) {
      setGlobalError('Failed to connect with our servers');
    }
  };

  const getUsers = async () => {
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        const users = jsonRes.map((user) => {
          user.canceledRides = user.phoneNum;
          delete user.phoneNum;

          user.completedRides = user.refreshToken;
          delete user.refreshToken;

          const dateFormat = timestampFormat(user.createdAt);
          user.createdAt = dateFormat.createdAt;
          user.month = dateFormat.month;

          return user;
        });
        setDispatch(SET_USERS, users.reverse());
      } else {
        if (jsonRes.statusCode === 401) {
          logAdminOut();
          setGlobalError('Your session has expired');
        } else setGlobalError(jsonRes.message);
      }
    } catch (err) {
      setGlobalError('Failed to connect with our servers');
    }
  };

  const getRides = async () => {
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/bookings/completed`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        const allMonths = [];
        const allDays = [];

        const allRides = jsonRes.map((ride) => {
          const dates = ride.date?.split(' ');

          allMonths.push(dates[0]);
          if (dates[0] === formattedMonth) {
            allDays.push(dates[1]);
          }

          ride.passengerImage = ride.rideStatus;
          delete ride.rideStatus;

          const dateFormat = timestampFormat(ride.createdAt);
          ride.month = dateFormat.month;

          return ride;
        });

        const monthStat = getThisMonthsRides(allMonths);
        const todayStat = getTodaysRides(allDays);
        setDispatch(SET_RIDES_REALTIME_STAT, {
          month: monthStat,
          today: todayStat,
        });

        setDispatch(SET_RIDES, allRides.reverse());
      } else {
        if (jsonRes.statusCode === 401) {
          logAdminOut();
          setGlobalError('Your session has expired');
        } else setGlobalError(jsonRes.message);
      }
    } catch (err) {
      setGlobalError('Failed to connect with our servers');
    }
  };

  const getDrivers = async () => {
    try {
      const res = await fetch(`${uri}/drivers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        const allDrivers = jsonRes.map((driver) => {
          const allMonths = [];
          const allDays = [];

          // get each driver's rides
          driver.rides.forEach((ride) => {
            const dates = ride.date.split(' ');

            allMonths.push(dates[0]);
            if (dates[0] === formattedMonth) {
              allDays.push(dates[1]);
            }
          });

          // get the rides for this month and day
          const monthStat = getThisMonthsRides(allMonths);
          const todayStat = getTodaysRides(allDays);

          driver.ridesToday = todayStat;
          driver.ridesThisMonth = monthStat;
          driver.completedRides = driver.rides.length;

          const dateFormat = timestampFormat(driver.createdAt);
          driver.month = dateFormat.month;
          driver.createdAt = dateFormat.createdAt;

          return driver;
        });

        setDispatch(SET_DRIVERS, allDrivers.reverse());
        // drivers are sort by most rides in sidebar, change this to options of most rides and success rate (stars)
      } else {
        console.log(jsonRes);
        setGlobalError(jsonRes.message);
      }
    } catch (err) {
      setGlobalError('Failed to connect with our servers');
    }
  };

  useEffect(() => {
    let isSubscribed = true;
    (async () => {
      isSubscribed && setInitLoading(true);
      await getFeedbacks();
      await getDrivers();
      await getUsers();
      await getRides();
      isSubscribed && setInitLoading(false);
    })();

    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line
  }, []);

  const getCarTypePrice = async (carType) => {
    try {
      const res = await fetch(`${uri}/prices/${carType}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setDispatch(SET_CURRENT_CAR_PRICES, { carType, price: jsonRes.msg });
      } else {
        if (jsonRes.statusCode === 401) {
          logAdminOut();
          setGlobalError('Your session has expired');
        } else setGlobalError(jsonRes.message);
      }
    } catch (err) {
      setGlobalError('Failed to connect with our servers');
    }
  };

  const approveDriver = async (carId) => {
    setLoading(true);
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/drivers/${carId}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        await getDrivers();
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const deleteDriver = async (carId) => {
    toFalse(SET_DELETE_SUCCESS);
    setSuccess(false);
    setLoading(true);

    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/drivers/${carId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setDispatch(SET_DELETE_SUCCESS, true);
        setSuccess('Driver deleted');
        await getDrivers();
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  // settings

  const changeDriverCarType = async (carId, newCarType) => {
    setSuccess(false);
    setLoading(true);
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/settings/car/${carId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
        body: JSON.stringify({ carType: newCarType }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setSuccess('Car type changed');
        await getDrivers();
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    setSuccess(false);
    setLoading(true);
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/settings/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
        body: JSON.stringify({ oldPassword, newPassword }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setSuccess('Password changed');
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const changeCarPrice = async (carType, newPrice) => {
    setSuccess(false);
    setLoading(true);
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      const res = await fetch(`${uri}/admin/settings/price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
        body: JSON.stringify({ carType, newPrice }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        await getCarTypePrice(carType);
        setSuccess(`Price for ${carType} cars has been changed`);
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  // actions
  const setDispatch = (type, payload) => {
    dispatch({
      type,
      payload,
    });
  };

  const logAdminOut = () => {
    deleteCookie('access_token');
    setDispatch(SET_AUTH_ERROR, true);
  };

  // set any desired state to false via its type
  const toFalse = (type) => {
    setDispatch(type, false);
  };

  const setGlobalError = (error) => {
    setDispatch(SET_GLOBAL_ERROR, error);
  };

  const setLoading = (bool) => {
    setDispatch(SET_LOADING, bool);
  };

  const setInitLoading = (bool) => {
    setDispatch(SET_INIT_LOADING, bool);
  };

  const setSuccess = (msg) => {
    setDispatch(SET_SUCCESS, msg);
  };

  return (
    <DashboardContext.Provider
      value={{
        admin: state.admin,
        globalError: state.globalError,
        error: state.error,
        loading: state.loading,
        initLoading: state.initLoading,
        authError: state.authError,
        success: state.success,
        feedbacks: state.feedbacks,
        users: state.users,
        drivers: state.drivers,
        rides: state.rides,
        ridesRealtimeStat: state.ridesRealtimeStat,
        currentPrices: state.currentPrices,
        deleteSuccess: state.deleteSuccess,
        approveDriver,
        deleteDriver,
        toFalse,
        getCarTypePrice,
        changeDriverCarType,
        setGlobalError,
        changePassword,
        changeCarPrice,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
